import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { ESTADO_PETICION, ESTADOS_PETICION_ACTIVOS, TIPO_LINEA } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import ClienteCombobox from "@/components/ClienteCombobox";
import { Plus, Search, FileStack, FileSpreadsheet, ChevronDown, X, FileText, Receipt, Download } from "lucide-react";
import ExportPeticionesDialog from "@/components/ExportPeticionesDialog";
import { ImportPeticionDialog } from "@/components/ImportPeticionDialog";
import ImportMapeoDialog from "@/components/ImportMapeoDialog";
import EmailToPeticionDialog from "@/components/EmailToPeticionDialog";
import PeticionRapidaDialog from "@/components/PeticionRapidaDialog";
import LearnerHint from "@/components/LearnerHint";
import GroupSortControls from "@/components/GroupSortControls";
import ProyectoThumb from "@/components/ProyectoThumb";
import BuscadorLineas from "@/components/BuscadorLineas";
import useResizableColumns from "@/hooks/useResizableColumns";
import { useNavigate } from "react-router-dom";
import { useEntityRefresh, broadcastEntityChange } from "@/lib/useEntityRefresh";
import { toast } from "sonner";

const COLS_DEFAULT_WIDTHS = [320, 220, 130, 220, 150, 130, 90]; // px
const COLS_STORAGE_KEY = "dnf-peticiones-cols-v2";

export default function PeticionesList() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [users, setUsers] = useState([]);
  // Iter 73 — Persistir filtros en sessionStorage (bug: al volver atrás se perdían)
  const FS_KEY = "dnf-peticiones-filtros-v1";
  const _fs = (() => {
    try { return JSON.parse(sessionStorage.getItem(FS_KEY) || "{}"); }
    catch { return {}; }
  })();
  const [filter, setFilter] = useState(_fs.filter || "");
  const [deepIds, setDeepIds] = useState(null); // ids de petición del buscador de líneas/componentes
  // Iter 96 — multi-select de estados. Set vacío = solo activas (default).
  // "todos" = "show all" toggle. Si tiene estados explícitos, los aplica con OR.
  const [estadoFilter, setEstadoFilter] = useState(_fs.estadoFilter || "activas");
  const [estadosSel, setEstadosSel] = useState(() => new Set(_fs.estadosSel || []));
  const [estadoPopOpen, setEstadoPopOpen] = useState(false);

  const toggleEstado = (k) => {
    const next = new Set(estadosSel);
    if (next.has(k)) next.delete(k); else next.add(k);
    setEstadosSel(next);
    setEstadoFilter(next.size > 0 ? "personalizado" : "activas");
  };
  const limpiarEstados = () => { setEstadosSel(new Set()); setEstadoFilter("activas"); };
  const [clienteFilter, setClienteFilter] = useState(_fs.clienteFilter || "todos");
  const [marcaFilter, setMarcaFilter] = useState(_fs.marcaFilter || "todas");
  const [dateField, setDateField] = useState(() => localStorage.getItem("dnf-peticiones-datefield") || "fecha_objetivo");
  const [dateFrom, setDateFrom] = useState(_fs.dateFrom || "");
  const [dateTo, setDateTo] = useState(_fs.dateTo || "");
  const [datePreset, setDatePreset] = useState(_fs.datePreset || "custom");
  useEffect(() => { localStorage.setItem("dnf-peticiones-datefield", dateField); }, [dateField]);
  // Persistir filtros volátiles en sessionStorage al cambiar cualquiera
  useEffect(() => {
    try {
      sessionStorage.setItem(FS_KEY, JSON.stringify({
        filter, estadoFilter, estadosSel: Array.from(estadosSel),
        clienteFilter, marcaFilter, dateFrom, dateTo, datePreset,
      }));
    } catch { /* quota / private mode */ }
  }, [filter, estadoFilter, estadosSel, clienteFilter, marcaFilter, dateFrom, dateTo, datePreset]);
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem("dnf-peticiones-groupby") || "none");
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("dnf-peticiones-sortby") || "fecha_objetivo");
  const [sortDir, setSortDir] = useState(() => localStorage.getItem("dnf-peticiones-sortdir") || "desc");
  useEffect(() => { localStorage.setItem("dnf-peticiones-groupby", groupBy); }, [groupBy]);
  useEffect(() => { localStorage.setItem("dnf-peticiones-sortby", sortBy); }, [sortBy]);
  useEffect(() => { localStorage.setItem("dnf-peticiones-sortdir", sortDir); }, [sortDir]);
  const { gridTemplate, startResize, resetWidths } = useResizableColumns(
    COLS_STORAGE_KEY, COLS_DEFAULT_WIDTHS, 60, { 0: 2, 1: 1 }
  );
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importMapeoOpen, setImportMapeoOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [form, setForm] = useState({
    nombre: "", cliente_id: "", comercial_id: "",
    descripcion: "", marca: "", email_origen: "", referencia_externa: "",
    estado: "borrador", fecha_objetivo: "", fecha_solicitud: new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => Promise.all([
    api.get("/peticiones"), api.get("/clientes"), api.get("/users")
  ]).then(([p, c, u]) => {
    setItems(p.data);
    setClientes([...c.data].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es")));
    setUsers(u.data);
  }), []);
  useEffect(() => { load(); }, [load]);

  // Iter 70 — refrescar al volver a la pestaña/window (fix estado obsoleto en listado)
  useEffect(() => {
    const onFocus = () => { load(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  // Iter 70 — y cuando otra vista (PeticionDetail) emita un cambio
  useEntityRefresh("peticion", load);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.fecha_objetivo) delete payload.fecha_objetivo;
      else payload.fecha_objetivo = new Date(payload.fecha_objetivo).toISOString();
      if (!payload.fecha_solicitud) delete payload.fecha_solicitud;
      else payload.fecha_solicitud = new Date(payload.fecha_solicitud).toISOString();
      Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
      const { data: creada } = await api.post("/peticiones", payload);
      setOpen(false);
      setForm({ nombre: "", cliente_id: "", comercial_id: "",
        estado: "borrador", fecha_objetivo: "", fecha_solicitud: new Date().toISOString().slice(0, 10),
        descripcion: "", marca: "", email_origen: "" });
      toast.success(`Petición ${creada.codigo || ""} creada · añade la primera línea`);
      broadcastEntityChange("peticion");
      nav(`/peticiones/${creada.id}`, { state: { abrirLinea: true } });   // detalle recién creado + modal «Nueva línea» abierto una sola vez
    } catch (err) {
      toast.error(err?.response?.data?.detail?.msg || err?.response?.data?.detail || "No se pudo crear la petición");
    } finally { setSaving(false); }
  };

  const deepSet = deepIds ? new Set(deepIds) : null;
  const filtered = items.filter((p) => {
    const txt = (p.nombre + " " + (p.codigo || "") + " " + (p.marca || "")).toLowerCase();
    if (filter && !txt.includes(filter.toLowerCase())) return false;
    if (deepSet && !deepSet.has(p.id)) return false;
    if (estadoFilter === "activas") {
      if (!ESTADOS_PETICION_ACTIVOS.has(p.estado)) return false;
    } else if (estadoFilter === "personalizado") {
      if (estadosSel.size > 0 && !estadosSel.has(p.estado)) return false;
    } else if (estadoFilter !== "todos" && p.estado !== estadoFilter) return false;
    if (clienteFilter !== "todos" && p.cliente_id !== clienteFilter) return false;
    if (marcaFilter !== "todas" && p.marca !== marcaFilter) return false;
    // Filtro por fechas (objetivo/solicitud/creación o CUALQUIER fecha de montaje de sus líneas)
    if (dateFrom || dateTo) {
      if (dateField === "fecha_montaje") {
        const fechas = p.fechas_montaje_info?.fechas || [];
        if (!fechas.length) return false;
        const alguna = fechas.some((f) => (!dateFrom || f >= dateFrom) && (!dateTo || f <= dateTo));
        if (!alguna) return false;
      } else {
        const f = (p[dateField] || "").slice(0, 10);
        if (!f) return false;  // sin fecha → fuera
        if (dateFrom && f < dateFrom) return false;
        if (dateTo && f > dateTo) return false;
      }
    }
    return true;
  });

  // Ordenar
  const clienteName = (id) => clientes.find((c) => c.id === id)?.nombre || "";
  const sortedFiltered = [...filtered].sort((a, b) => {
    let va, vb;
    switch (sortBy) {
      case "fecha_objetivo":
        va = a.fecha_objetivo || ""; vb = b.fecha_objetivo || ""; break;
      case "presupuesto":
        va = a.presupuesto_calculado || 0; vb = b.presupuesto_calculado || 0; break;
      case "codigo":
        va = a.codigo || ""; vb = b.codigo || ""; break;
      case "nombre":
        va = (a.nombre || "").toLowerCase(); vb = (b.nombre || "").toLowerCase(); break;
      case "cliente":
        va = clienteName(a.cliente_id).toLowerCase();
        vb = clienteName(b.cliente_id).toLowerCase(); break;
      case "estado":
        va = a.estado || ""; vb = b.estado || ""; break;
      default:
        va = a.fecha_objetivo || ""; vb = b.fecha_objetivo || "";
    }
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  // Agrupar
  const groupKeyOf = (p) => {
    switch (groupBy) {
      case "cliente": return clienteName(p.cliente_id) || "Sin cliente";
      case "estado": return (ESTADO_PETICION[p.estado]?.label) || (p.estado || "Sin estado");
      case "marca": return p.marca || "Sin marca";
      case "mes":
        if (!p.fecha_objetivo) return "Sin fecha";
        try {
          const d = new Date(p.fecha_objetivo);
          return d.toLocaleDateString("es-ES", { year: "numeric", month: "long" });
        } catch (_e) { return "Sin fecha"; }
      default: return null;
    }
  };
  const grouped = (() => {
    if (groupBy === "none") return [{ key: null, items: sortedFiltered }];
    const map = new Map();
    for (const p of sortedFiltered) {
      const k = groupKeyOf(p) || "—";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(p);
    }
    return Array.from(map.entries()).sort(([a], [b]) => String(a).localeCompare(String(b)))
      .map(([key, items]) => ({ key, items }));
  })();

  const marcasDisponibles = Array.from(new Set(items.map((p) => p.marca).filter(Boolean))).sort();

  // Exportación Excel · selección múltiple (no persistida) y orden EXACTO del listado (grupos → orden)
  const idsFiltradasOrdenadas = grouped.flatMap((g) => g.items.map((p) => p.id));
  const setFiltradas = new Set(idsFiltradasOrdenadas);
  const nSelFiltradas = idsFiltradasOrdenadas.filter((id) => selectedIds.has(id)).length;
  const nSelFueraFiltro = selectedIds.size - nSelFiltradas;
  const todasFiltradasSel = idsFiltradasOrdenadas.length > 0 && nSelFiltradas === idsFiltradasOrdenadas.length;
  const idsSeleccionadasOrdenadas = [
    ...idsFiltradasOrdenadas.filter((id) => selectedIds.has(id)),
    ...items.map((p) => p.id).filter((id) => selectedIds.has(id) && !setFiltradas.has(id)),
  ];
  const toggleSel = (id) => setSelectedIds((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleTodasFiltradas = () => setSelectedIds((prev) => {
    const n = new Set(prev);
    if (todasFiltradasSel) idsFiltradasOrdenadas.forEach((id) => n.delete(id)); else idsFiltradasOrdenadas.forEach((id) => n.add(id));
    return n;
  });
  const filtrosDescripcion = {
    Texto: filter || "—",
    "Buscador profundo": deepIds ? `${deepIds.length} peticiones por líneas/componentes` : "—",
    Estado: estadoFilter === "personalizado" ? Array.from(estadosSel).map((k) => ESTADO_PETICION[k]?.label || k).join(", ") : estadoFilter,
    Cliente: clienteFilter === "todos" ? "todos" : clienteName(clienteFilter),
    Marca: marcaFilter,
    Fechas: (dateFrom || dateTo) ? `${dateField}: ${dateFrom || "…"} → ${dateTo || "…"}` : "—",
    Orden: `${sortBy} ${sortDir}`, Agrupación: groupBy,
  };

  const comerciales = users.filter((u) => (u.roles || []).includes("comercial") || (u.roles || []).includes("admin"));

  return (
    <div className="p-8 max-w-[1800px] mx-auto">
      <PageHeader
        title="Solicitudes de trabajo"
        subtitle="Peticiones: campañas y trabajos de producción gráfica"
        action={
          <div className="flex gap-2">
            <PeticionRapidaDialog clientes={clientes} onCreated={() => load()} />
            <EmailToPeticionDialog onCreated={() => nav("/peticiones/bandeja-ia")} />
            <Button onClick={() => setExportOpen(true)} variant="outline"
                    className="bg-transparent border-emerald-700/50 text-emerald-300 hover:bg-emerald-500/10"
                    title="Exportar a Excel (.xlsx) las peticiones filtradas, seleccionadas o todas"
                    data-testid="export-peticiones-btn">
              <Download className="w-4 h-4 mr-2" /> Exportar Excel
            </Button>
            <Button onClick={() => setImportOpen(true)} variant="outline"
                    className="bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-900"
                    data-testid="import-peticion-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Importar Excel
            </Button>
            <Button onClick={() => setImportMapeoOpen(true)} variant="outline"
                    className="bg-transparent border-cyan-700/50 text-cyan-300 hover:bg-cyan-500/10"
                    title="Importa montajes masivos desde cualquier Excel del cliente eligiendo qué columna corresponde a cada campo"
                    data-testid="import-mapeo-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Importar con mapeo
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <LearnerHint label="Crear una nueva petición/campaña">
                <Button className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold"
                  data-testid="new-peticion-btn">
                  <Plus className="w-4 h-4 mr-2" /> Nueva petición
                </Button>
              </LearnerHint>
            </DialogTrigger>
            <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl">
              <DialogHeader><DialogTitle>Nueva petición</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Nombre de la campaña *</Label>
                    <Input data-testid="form-peticion-nombre" required value={form.nombre}
                      onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                      placeholder="Copia el asunto del email del cliente"
                      className="bg-zinc-900 border-zinc-800" />
                    <div className="text-[10px] text-zinc-500 mt-1">El código interno se generará automáticamente (DNF-AÑO-NNNN)</div>
                  </div>
                  <div>
                    <Label>Marca / Producto</Label>
                    <Input value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })}
                      data-testid="form-peticion-marca"
                      placeholder='ej. "YSL Libre Berry Crush"'
                      className="bg-zinc-900 border-zinc-800" />
                  </div>
                  <div>
                    <Label>Email origen</Label>
                    <Input value={form.email_origen} onChange={(e) => setForm({ ...form, email_origen: e.target.value })}
                      placeholder="ej. floren@decorgalva.com"
                      className="bg-zinc-900 border-zinc-800" />
                  </div>
                  <div>
                    <Label>Cliente *</Label>
                    <ClienteCombobox
                      value={form.cliente_id}
                      onChange={(id) => setForm((f) => ({ ...f, cliente_id: id }))}
                      clientes={clientes}
                      testId="form-peticion-cliente"
                    />
                  </div>
                  <div>
                    <Label>Estado</Label>
                    <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                        {Object.entries(ESTADO_PETICION).filter(([k]) => !["presupuesto", "presupuesto_enviado", "pendiente", "planificada", "finalizada", "cerrada"].includes(k)).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Comercial</Label>
                    <Select value={form.comercial_id} onValueChange={(v) => setForm({ ...form, comercial_id: v })}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                        {comerciales.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Fecha solicitud</Label>
                    <Input type="date" value={form.fecha_solicitud} onChange={(e) => setForm({ ...form, fecha_solicitud: e.target.value })}
                      className="bg-zinc-900 border-zinc-800" />
                  </div>
                  <div>
                    <Label>Fecha objetivo (entrega)</Label>
                    <Input type="date" value={form.fecha_objetivo} onChange={(e) => setForm({ ...form, fecha_objetivo: e.target.value })}
                      className="bg-zinc-900 border-zinc-800" />
                  </div>
                  <div className="col-span-2">
                    <Label>Descripción</Label>
                    <Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                      className="bg-zinc-900 border-zinc-800" rows={3} />
                  </div>
                  <div className="col-span-2 bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 text-xs text-zinc-500">
                    💡 <span className="text-zinc-300 font-medium">Presupuesto y coste</span> se calculan automáticamente sumando los componentes que añadas (precio × cantidad).
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}
                    className="bg-transparent border-zinc-700">Cancelar</Button>
                  <Button type="submit" disabled={saving || !form.cliente_id}
                    data-testid="form-peticion-submit"
                    className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
                    {saving ? "Creando..." : "Crear petición"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <ImportPeticionDialog open={importOpen} onOpenChange={setImportOpen}
        onCreated={(d) => { load(); }} />

      <ImportMapeoDialog open={importMapeoOpen} onOpenChange={setImportMapeoOpen}
        onCreated={(d) => { load(); }} />
      <ExportPeticionesDialog open={exportOpen} onClose={() => setExportOpen(false)} idsFiltradas={idsFiltradasOrdenadas}
        idsSeleccionadas={idsSeleccionadasOrdenadas} totalCargadas={items.length} filtrosDescripcion={filtrosDescripcion} />

      {/* Buscador profundo de líneas / componentes */}
      <BuscadorLineas onResults={setDeepIds} />

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input placeholder="Buscar por nombre, código o marca..."
            value={filter} onChange={(e) => setFilter(e.target.value)}
            data-testid="peticiones-search"
            className="pl-9 bg-zinc-900/70 border-zinc-800" />
        </div>
        {/* Iter 96 — Multi-select de estados con Popover + checkboxes */}
        <Popover open={estadoPopOpen} onOpenChange={setEstadoPopOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline"
              data-testid="peticiones-filter-estado"
              className={`w-56 justify-between bg-zinc-900/70 border-zinc-800 text-zinc-200 font-normal ${estadosSel.size > 0 ? "border-cyan-500/40" : ""}`}>
              <span className="truncate">
                {estadoFilter === "activas" && "🟢 Activas (por defecto)"}
                {estadoFilter === "todos" && "Todos los estados"}
                {estadoFilter === "personalizado" && (
                  estadosSel.size === 1
                    ? (ESTADO_PETICION[Array.from(estadosSel)[0]]?.label || Array.from(estadosSel)[0])
                    : `${estadosSel.size} estados`
                )}
              </span>
              <ChevronDown className="w-3.5 h-3.5 ml-2 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="bg-zinc-950 border-zinc-800 text-zinc-100 w-64 p-0" align="start">
            <div className="p-2 border-b border-zinc-800 flex items-center justify-between gap-2">
              <button onClick={() => { setEstadoFilter("activas"); setEstadosSel(new Set()); setEstadoPopOpen(false); }}
                className={`flex-1 text-xs px-2 py-1 rounded ${estadoFilter === "activas" ? "bg-cyan-500/20 text-cyan-200" : "text-zinc-300 hover:bg-zinc-900"}`}
                data-testid="peticiones-estado-activas">🟢 Activas</button>
              <button onClick={() => { setEstadoFilter("todos"); setEstadosSel(new Set()); setEstadoPopOpen(false); }}
                className={`flex-1 text-xs px-2 py-1 rounded ${estadoFilter === "todos" ? "bg-cyan-500/20 text-cyan-200" : "text-zinc-300 hover:bg-zinc-900"}`}
                data-testid="peticiones-estado-todos">Todos</button>
            </div>
            <div className="max-h-72 overflow-y-auto p-1">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 px-2 py-1">Selección personalizada (varios):</div>
              {Object.entries(ESTADO_PETICION).filter(([k]) => !["presupuesto", "presupuesto_enviado", "pendiente", "planificada", "finalizada", "cerrada"].includes(k)).map(([k, v]) => (
                <label key={k}
                  data-testid={`peticiones-estado-${k}`}
                  className="flex items-center gap-2 px-2 py-1.5 text-xs text-zinc-200 hover:bg-zinc-900 rounded cursor-pointer">
                  <input type="checkbox" checked={estadosSel.has(k)}
                    onChange={() => toggleEstado(k)}
                    className="accent-cyan-500" />
                  <span>{v.label}</span>
                </label>
              ))}
            </div>
            {estadosSel.size > 0 && (
              <div className="p-2 border-t border-zinc-800">
                <button onClick={limpiarEstados} className="text-xs text-zinc-400 hover:text-zinc-100"
                  data-testid="peticiones-estado-clear">
                  ✕ Limpiar selección ({estadosSel.size})
                </button>
              </div>
            )}
          </PopoverContent>
        </Popover>
        <Select value={clienteFilter} onValueChange={setClienteFilter}>
          <SelectTrigger data-testid="peticiones-filter-cliente" className="w-56 bg-zinc-900/70 border-zinc-800"><SelectValue placeholder="Cliente" /></SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[300px]">
            <SelectItem value="todos">Todos los clientes</SelectItem>
            {/* Sólo clientes que tienen al menos una petición — orden alfabético */}
            {(() => {
              const counts = items.reduce((acc, p) => {
                if (p.cliente_id) acc[p.cliente_id] = (acc[p.cliente_id] || 0) + 1;
                return acc;
              }, {});
              const clientesConPet = clientes
                .filter((c) => counts[c.id])
                .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es"));
              return clientesConPet.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.nombre} <span className="text-zinc-500 text-[10px]">({counts[c.id]})</span>
                </SelectItem>
              ));
            })()}
          </SelectContent>
        </Select>
        <Select value={marcaFilter} onValueChange={setMarcaFilter}>
          <SelectTrigger data-testid="peticiones-filter-marca" className="w-44 bg-zinc-900/70 border-zinc-800"><SelectValue placeholder="Marca" /></SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[300px]">
            <SelectItem value="todas">Todas las marcas</SelectItem>
            {marcasDisponibles.length === 0 && (
              <SelectItem value="__sin_marcas" disabled>No hay peticiones con marca</SelectItem>
            )}
            {marcasDisponibles.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Filtros por fecha (rango) */}
      <div className="flex flex-wrap items-center gap-2 mb-3 bg-zinc-900/40 border border-zinc-800/60 rounded-lg p-2.5">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-1">Fechas:</span>
        <Select value={dateField} onValueChange={setDateField}>
          <SelectTrigger data-testid="peticiones-filter-date-field" className="w-44 h-9 bg-zinc-900/70 border-zinc-800 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
            <SelectItem value="fecha_objetivo">Fecha objetivo</SelectItem>
            <SelectItem value="fecha_montaje">Fecha de montaje (líneas)</SelectItem>
            <SelectItem value="fecha_solicitud">Fecha solicitud</SelectItem>
            <SelectItem value="fecha_creacion">Fecha creación</SelectItem>
          </SelectContent>
        </Select>
        <Select value={datePreset} onValueChange={(v) => {
          setDatePreset(v);
          const today = new Date();
          const iso = (d) => d.toISOString().slice(0, 10);
          if (v === "custom") return; // mantener actuales
          if (v === "all") { setDateFrom(""); setDateTo(""); return; }
          if (v === "today") { const t = iso(today); setDateFrom(t); setDateTo(t); return; }
          if (v === "tomorrow") { const d = new Date(today); d.setDate(d.getDate() + 1); const t = iso(d); setDateFrom(t); setDateTo(t); return; }
          if (v === "this_week") {
            const day = today.getDay() || 7; // lunes=1, domingo=7
            const start = new Date(today); start.setDate(today.getDate() - day + 1);
            const end = new Date(start); end.setDate(start.getDate() + 6);
            setDateFrom(iso(start)); setDateTo(iso(end)); return;
          }
          if (v === "next_week") {
            const day = today.getDay() || 7;
            const start = new Date(today); start.setDate(today.getDate() - day + 1 + 7);
            const end = new Date(start); end.setDate(start.getDate() + 6);
            setDateFrom(iso(start)); setDateTo(iso(end)); return;
          }
          if (v === "this_month") {
            const start = new Date(today.getFullYear(), today.getMonth(), 1);
            const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            setDateFrom(iso(start)); setDateTo(iso(end)); return;
          }
          if (v === "next_month") {
            const start = new Date(today.getFullYear(), today.getMonth() + 1, 1);
            const end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
            setDateFrom(iso(start)); setDateTo(iso(end)); return;
          }
          if (v === "last_30") {
            const end = new Date(today);
            const start = new Date(today); start.setDate(today.getDate() - 30);
            setDateFrom(iso(start)); setDateTo(iso(end)); return;
          }
          if (v === "overdue") {
            // Vencidas: fecha objetivo < hoy y estado activo
            const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
            setDateFrom(""); setDateTo(iso(yesterday)); return;
          }
        }}>
          <SelectTrigger data-testid="peticiones-filter-date-preset" className="w-40 h-9 bg-zinc-900/70 border-zinc-800 text-xs">
            <SelectValue placeholder="Rango rápido" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
            <SelectItem value="custom">Personalizado</SelectItem>
            <SelectItem value="all">Sin filtro de fecha</SelectItem>
            <SelectItem value="today">Hoy</SelectItem>
            <SelectItem value="tomorrow">Mañana</SelectItem>
            <SelectItem value="this_week">Esta semana</SelectItem>
            <SelectItem value="next_week">Próxima semana</SelectItem>
            <SelectItem value="this_month">Este mes</SelectItem>
            <SelectItem value="next_month">Próximo mes</SelectItem>
            <SelectItem value="last_30">Últimos 30 días</SelectItem>
            <SelectItem value="overdue">⏰ Vencidas (antes de hoy)</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-zinc-500">desde</span>
          <Input type="date" value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setDatePreset("custom"); }}
            data-testid="peticiones-filter-date-from"
            className="w-40 h-9 bg-zinc-900/70 border-zinc-800 text-xs" />
          <span className="text-[10px] text-zinc-500">hasta</span>
          <Input type="date" value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setDatePreset("custom"); }}
            data-testid="peticiones-filter-date-to"
            className="w-40 h-9 bg-zinc-900/70 border-zinc-800 text-xs" />
          {(dateFrom || dateTo) && (
            <button type="button"
              onClick={() => { setDateFrom(""); setDateTo(""); setDatePreset("all"); }}
              data-testid="peticiones-filter-date-clear"
              title="Limpiar filtro de fechas"
              className="h-9 w-9 inline-flex items-center justify-center rounded-md bg-zinc-900/70 border border-zinc-800 hover:bg-zinc-800 text-zinc-300">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Agrupar / Ordenar */}
      <div className="flex items-center justify-between mb-3">
        <GroupSortControls
          testIdPrefix="peticiones"
          groupBy={groupBy} setGroupBy={setGroupBy}
          groupOptions={[
            { value: "cliente", label: "Por cliente" },
            { value: "estado", label: "Por estado" },
            { value: "marca", label: "Por marca" },
            { value: "mes", label: "Por mes (fecha objetivo)" },
          ]}
          sortBy={sortBy} setSortBy={setSortBy}
          sortOptions={[
            { value: "fecha_objetivo", label: "Fecha objetivo" },
            { value: "presupuesto", label: "Importe" },
            { value: "codigo", label: "Código" },
            { value: "nombre", label: "Nombre" },
            { value: "cliente", label: "Cliente (A-Z)" },
            { value: "estado", label: "Estado" },
          ]}
          sortDir={sortDir} setSortDir={setSortDir}
          onReset={() => { setGroupBy("none"); setSortBy("fecha_objetivo"); setSortDir("desc"); resetWidths(); }}
        />
        <div className="text-[10px] text-zinc-500 flex items-center gap-3 flex-wrap" data-testid="peticiones-count">
          <span>{sortedFiltered.length} {sortedFiltered.length === 1 ? "petición" : "peticiones"}</span>
          {selectedIds.size > 0 && (
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-amber-200 normal-case tracking-normal" data-testid="pet-seleccion-bar">
              <b data-testid="pet-seleccion-count">{selectedIds.size}</b> seleccionada{selectedIds.size === 1 ? "" : "s"}
              {nSelFueraFiltro > 0 && <span className="text-amber-400/80" data-testid="pet-seleccion-fuera">({nSelFueraFiltro} fuera del filtro actual)</span>}
              <button type="button" onClick={() => setExportOpen(true)} data-testid="pet-seleccion-exportar" className="underline hover:text-amber-100">Exportar selección</button>
              <button type="button" onClick={() => setSelectedIds(new Set())} data-testid="pet-seleccion-limpiar" className="hover:text-amber-100">✕ Limpiar</button>
            </span>
          )}
        </div>
      </div>

      {/* List */}
      <div data-testid="peticiones-list" className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="grid gap-4 px-5 py-3 text-[10px] uppercase tracking-[0.16em] text-zinc-500 border-b border-zinc-800 bg-zinc-950/50 relative"
          style={{ gridTemplateColumns: gridTemplate }}>
          {[
            { label: "Campaña",        field: "nombre" },
            { label: "Cliente",        field: "cliente" },
            { label: "Código",         field: "codigo" },
            { label: "Presupuesto",    field: "presupuesto" },
            { label: "Fecha objetivo", field: "fecha_objetivo" },
            { label: "Estado",         field: "estado" },
            { label: "Docs",           field: "docs",  noSort: true },
          ].map(({ label, field, noSort }, i) => {
            const active = sortBy === field;
            const onSort = () => {
              if (sortBy !== field) { setSortBy(field); setSortDir("asc"); return; }
              setSortDir(sortDir === "asc" ? "desc" : "asc");
            };
            return (
              <div key={label}
                   onClick={noSort ? undefined : onSort}
                   data-testid={`th-pet-${field}`}
                   className={`relative truncate pr-2 ${noSort ? "" : "cursor-pointer select-none hover:text-zinc-200"} transition-colors ${active ? "text-amber-300" : ""}`}>
                <span className="inline-flex items-center gap-1">
                  {i === 0 && (
                    <input type="checkbox" data-testid="pet-select-all" title="Seleccionar / deseleccionar todas las filtradas"
                      className="accent-amber-500 mr-1 cursor-pointer" checked={todasFiltradasSel} ref={(el) => { if (el) el.indeterminate = !todasFiltradasSel && nSelFiltradas > 0; }}
                      onClick={(e) => e.stopPropagation()} onChange={toggleTodasFiltradas} />
                  )}
                  {label}
                  {!noSort && (
                    <span className={`text-[9px] ${active ? "opacity-100" : "opacity-30"}`}>
                      {!active ? "↕" : (sortDir === "asc" ? "▲" : "▼")}
                    </span>
                  )}
                </span>
                {i < 6 && (
                  <span
                    onMouseDown={startResize(i)}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={resetWidths}
                    data-testid={`peticiones-col-resize-${i}`}
                    title="Arrastra para redimensionar · Doble-click reset"
                    className="absolute right-0 top-0 h-full w-2 -mr-2 cursor-col-resize hover:bg-amber-500/40 active:bg-amber-500/70 transition-colors z-10"
                  />
                )}
              </div>
            );
          })}
        </div>
        {grouped.map((grp, gi) => (
          <React.Fragment key={grp.key ?? `grp-${gi}`}>
            {grp.key !== null && (
              <div data-testid={`peticiones-group-${grp.key}`}
                className="px-5 py-2 bg-zinc-950/40 border-b border-zinc-800/60 text-[10px] uppercase tracking-[0.16em] text-amber-300/80 font-semibold flex items-center justify-between">
                <span>{grp.key}</span>
                <span className="text-zinc-500">{grp.items.length}</span>
              </div>
            )}
            {grp.items.map((p) => {
              const cli = clientes.find((c) => c.id === p.cliente_id);
              const est = ESTADO_PETICION[p.estado] || ESTADO_PETICION.borrador;
              return (
                <Link key={p.id} to={`/peticiones/${p.id}`}
                  data-testid={`peticion-row-${p.codigo || p.id}`}
                  className="grid gap-4 px-5 py-4 items-center border-b border-zinc-800/60 hover:bg-zinc-900/60 transition-colors last:border-b-0"
                  style={{ gridTemplateColumns: gridTemplate }}>
                  <div className="min-w-0 flex items-center gap-3">
                    <input type="checkbox" checked={selectedIds.has(p.id)} data-testid={`pet-select-${p.codigo || p.id}`}
                      className="accent-amber-500 cursor-pointer shrink-0" title="Seleccionar para exportar"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSel(p.id); }} onChange={() => {}} />
                    <ProyectoThumb url={p.imagen_url} nombre={cli?.nombre || p.nombre} size={38}
                      data-testid={`peticion-thumb-${p.codigo || p.id}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-zinc-100 truncate flex items-center gap-2">
                        {p.nombre}
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {p.marca && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/30">🏷 {p.marca}</span>}
                      {p.revision_manual && (
                        <span
                          data-testid={`revision-manual-${p.codigo || p.id}`}
                          className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40 animate-pulse"
                          title={p.revision_manual_motivo || "Requiere revisión manual"}>
                          ⚠️ Revisión manual
                        </span>
                      )}
                      {/* Iter 84.5 — Badge con tipo(s) de trabajo: Montaje, Producción, Diseño, etc. */}
                      {(p.tipos_lineas || []).map((t) => {
                        const meta = TIPO_LINEA?.[t];
                        const label = meta?.label || t.replace(/_/g, " ");
                        const cls = meta?.color || "bg-zinc-800 text-zinc-300";
                        return (
                          <span key={t}
                            data-testid={`tipo-trabajo-${p.codigo || p.id}-${t}`}
                            className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}
                            title="Tipo de trabajo">
                            {label}
                          </span>
                        );
                      })}
                      {p.descripcion && <span className="text-[11px] text-zinc-500 truncate">{p.descripcion}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-zinc-300 truncate">{cli?.nombre || "—"}</div>
                  <div className="text-xs text-zinc-400 font-mono">{p.codigo || "—"}</div>
                  <div className="text-sm text-zinc-200">
                    {(p.presupuesto_calculado || 0).toLocaleString("es-ES", { minimumFractionDigits: 2 })} €
                    <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-zinc-500">
                      <span data-testid={`num-lineas-${p.codigo || p.id}`} title="Líneas de trabajo (puntos de montaje)">
                        📍 {p.num_lineas || 0} líneas
                      </span>
                      {p.num_componentes ? <span title="Componentes (materiales)">· {p.num_componentes} comp.</span> : null}
                    </div>
                    {/* Iter 98 · Badge facturación FD (si la petición ya está enlazada con factura) */}
                    {p.factura_fd?.fd_id && (
                      <FacturaFdBadge factura={p.factura_fd} presupuesto={p.presupuesto_calculado || 0} codigo={p.codigo} />
                    )}
                    {/* Progreso de montaje: una línea puede dividirse en N paradas (rutas/días/montadores).
                        Una línea sólo se considera "montada" cuando TODAS sus paradas están aprobadas. */}
                    {p.num_lineas > 0 && (
                      <div className="flex items-center gap-2 mt-1.5" data-testid={`progreso-${p.codigo || p.id}`}>
                        <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden flex">
                          {p.lineas_montadas > 0 && (
                            <div className="bg-emerald-500" style={{ width: `${(p.lineas_montadas / p.num_lineas) * 100}%` }}
                              title={`${p.lineas_montadas} líneas montadas`} />
                          )}
                          {p.lineas_en_progreso > 0 && (
                            <div className="bg-amber-400" style={{ width: `${(p.lineas_en_progreso / p.num_lineas) * 100}%` }}
                              title={`${p.lineas_en_progreso} en progreso`} />
                          )}
                        </div>
                        <div className="text-[10px] font-mono">
                          <span className="text-emerald-400" title="Líneas con todas sus paradas aprobadas">✓{p.lineas_montadas || 0}</span>
                          {p.lineas_en_progreso > 0 && <span className="text-amber-400 ml-1" title="Líneas con alguna parada aprobada pero faltan más">↻{p.lineas_en_progreso}</span>}
                          <span className="text-zinc-500 ml-1" title="Líneas sin ninguna parada aprobada (o sin asignar a ruta)">·{p.lineas_pendientes || 0}</span>
                        </div>
                      </div>
                    )}
                    {p.componentes_pendientes_aaff > 0 && (
                      <div className="text-[10px] text-red-400 mt-0.5">⏳ {p.componentes_pendientes_aaff} AAFF</div>
                    )}
                    {/* Iter 110 — Estado de entrega de materiales (vista rápida) */}
                    <EntregaMaterialesBadge stats={p.entrega_stats} codigo={p.codigo || p.id} />
                  </div>
                  <div className="text-xs text-zinc-400">
                    {p.fecha_objetivo ? new Date(p.fecha_objetivo).toLocaleDateString("es-ES") : "—"}
                    {/* Iter 111 · Badge cuando líneas tienen fechas distintas o desalineadas */}
                    <FechasLineasBadge info={p.fechas_montaje_info} codigo={p.codigo || p.id} />
                  </div>
                  <div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${est.color}`}>
                      {est.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5"
                       data-testid={`docs-${p.codigo || p.id}`}>
                    <DocBadge
                      tipo="albaran"
                      doc={p.doc_albaran}
                      peticionId={p.id}
                    />
                    <DocBadge
                      tipo="factura"
                      doc={p.doc_factura}
                      peticionId={p.id}
                    />
                  </div>
                </Link>
              );
            })}
          </React.Fragment>
        ))}
        {sortedFiltered.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-zinc-500">
            {items.length === 0 ? "No hay peticiones. Crea la primera." : "No se encontraron resultados."}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Doc badge (albarán / factura) en el listado ─────────────────────
// Iter 70 — botoncitos clicables con icono + tooltip. Si no hay doc, gris.
function DocBadge({ tipo, doc, peticionId }) {
  const isFactura = tipo === "factura";
  const Icon = isFactura ? Receipt : FileText;
  const label = isFactura ? "Factura" : "Albarán";
  if (!doc) {
    return (
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded border border-zinc-800 bg-zinc-900/40 text-zinc-700"
        title={`Sin ${label.toLowerCase()}`}
        data-testid={`doc-${tipo}-empty-${peticionId}`}>
        <Icon className="w-3.5 h-3.5" />
      </span>
    );
  }
  const numero = doc.fd_numero || doc.numero || (isFactura ? doc.id?.slice(0, 6) : doc.id?.slice(0, 6)) || "—";
  const titleParts = [
    `${label}: ${numero}`,
    isFactura && doc.fd_total ? `Total: ${doc.fd_total} €` : null,
    !isFactura && doc.estado ? `Estado: ${doc.estado}` : null,
  ].filter(Boolean);
  const color = isFactura
    ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
    : "border-sky-500/40 text-sky-300 hover:bg-sky-500/10";

  const openPdf = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Si la factura ya tiene URL PDF en Cloudinary/FD, abrir directo (es público)
    if (isFactura && doc.fd_pdf_url) {
      window.open(doc.fd_pdf_url, "_blank", "noopener");
      return;
    }
    // En caso contrario, descargar PDF autenticado a través del backend → blob URL
    const endpoint = isFactura
      ? (doc.id ? `/facturas/${doc.id}/pdf` : null)
      : (doc.id ? `/albaranes/${doc.id}/pdf` : null);
    if (!endpoint) {
      toast.error(`Sin PDF disponible para este ${label.toLowerCase()}`);
      return;
    }
    try {
      const res = await api.get(endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, "_blank", "noopener");
      if (!w) toast.error("Bloqueo de pop-up · permite ventanas emergentes");
      // Liberar después de un rato (el navegador ya tiene el blob)
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Error abriendo ${label.toLowerCase()}`);
    }
  };

  return (
    <button
      type="button"
      onClick={openPdf}
      title={titleParts.join(" · ")}
      data-testid={`doc-${tipo}-${peticionId}`}
      className={`inline-flex items-center justify-center w-7 h-7 rounded border ${color} bg-zinc-900/40 transition-colors`}>
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}


// Iter 111 · Badge fechas de montaje distintas / desalineadas con objetivo
function FechasLineasBadge({ info, codigo }) {
  const [abierto, setAbierto] = useState(false);
  if (!info) return null;
  const { distintas = 0, fechas = [], detalle = [], sin_fecha = 0, desalineadas_con_objetivo = 0, mezcladas = false } = info;
  if (distintas === 0 && sin_fecha === 0) return null;
  const fmt = (f) => new Date(f + "T00:00:00").toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" });
  const label = mezcladas
    ? `📅 ${distintas} fechas`
    : distintas === 1
      ? `📅 ${fmt(fechas[0])}${desalineadas_con_objetivo > 0 ? " ⚠️" : ""}`
      : `⏳ ${sin_fecha} sin fecha`;
  const cls = mezcladas
    ? "bg-cyan-500/15 text-cyan-300 border-cyan-500/30"
    : (desalineadas_con_objetivo > 0
        ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
        : "bg-zinc-800 text-zinc-400 border-zinc-700");
  return (
    <div className="mt-1" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      <Popover open={abierto} onOpenChange={setAbierto}>
        <PopoverTrigger asChild>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAbierto((v) => !v); }}
            className={`inline-block text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border cursor-pointer hover:brightness-125 transition-[filter] ${cls}`}
            title="Ver el desglose de fechas de montaje de esta petición"
            data-testid={`fechas-lineas-${codigo}`}>
            {label} {distintas > 0 && "▾"}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="bg-zinc-950 border-zinc-800 text-zinc-100 w-72 p-2.5"
          data-testid={`fechas-lineas-pop-${codigo}`}>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Fechas de montaje · {codigo}</div>
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {detalle.map((d) => (
              <div key={d.fecha} className="border border-zinc-800 rounded p-1.5">
                <div className="text-xs font-bold text-cyan-300">📅 {fmt(d.fecha)} <span className="text-zinc-500 font-normal">· {d.n} montaje(s)</span></div>
                <div className="text-[10px] text-zinc-400 mt-0.5">
                  {d.espacios.join(" · ")}{d.n > d.espacios.length ? ` · +${d.n - d.espacios.length} más` : ""}
                </div>
              </div>
            ))}
            {sin_fecha > 0 && (
              <div className="text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded p-1.5">
                ⏳ {sin_fecha} línea(s) todavía sin fecha de montaje
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Iter 110 · Estado de entrega de materiales — contador con iconos + tooltip
// Muestra 4 categorías: ✅ entregado · 🏪 en PDV · 📦 en almacén · ⏳ pendiente
// Solo pinta las categorías con conteo > 0 para no saturar el listado.
function EntregaMaterialesBadge({ stats, codigo }) {
  if (!stats || !stats.total) return null;
  const { total, entregado = 0, en_pdv = 0, en_almacen = 0, pendiente = 0 } = stats;
  const tramos = [
    { key: "entregado", n: entregado, icon: "✅", label: "Entregado / Instalado",
      cls: "text-emerald-400", bar: "bg-emerald-500" },
    { key: "en_pdv", n: en_pdv, icon: "🏪", label: "En PDV",
      cls: "text-sky-300", bar: "bg-sky-500" },
    { key: "en_almacen", n: en_almacen, icon: "📦", label: "En almacén / Listo para cargar",
      cls: "text-amber-300", bar: "bg-amber-400" },
    { key: "pendiente", n: pendiente, icon: "⏳", label: "En producción / Pendiente",
      cls: "text-zinc-400", bar: "bg-zinc-500" },
  ];
  const tooltip = tramos
    .filter((t) => t.n > 0)
    .map((t) => `${t.icon} ${t.label}: ${t.n}`)
    .join("\n") + `\n— Total materiales: ${total}`;
  const todosEntregados = entregado === total;
  return (
    <div
      className="flex items-center gap-2 mt-1"
      title={tooltip}
      data-testid={`entrega-mat-${codigo}`}>
      {/* Mini barra segmentada por categoría */}
      <div className="flex-1 h-1 bg-zinc-800/60 rounded-full overflow-hidden flex">
        {tramos.map((t) => t.n > 0 && (
          <div key={t.key} className={t.bar}
            style={{ width: `${(t.n / total) * 100}%` }} />
        ))}
      </div>
      {/* Contadores compactos: solo los > 0 */}
      <div className="flex items-center gap-1.5 text-[10px] font-mono whitespace-nowrap">
        {todosEntregados ? (
          <span className="text-emerald-400" data-testid={`entrega-todo-${codigo}`}>
            ✅ {entregado}/{total}
          </span>
        ) : (
          tramos.filter((t) => t.n > 0).map((t) => (
            <span key={t.key} className={t.cls}
              data-testid={`entrega-${t.key}-${codigo}`}>
              {t.icon}{t.n}
            </span>
          ))
        )}
      </div>
    </div>
  );
}

function FacturaFdBadge({ factura, presupuesto, codigo }) {
  const importeReal = Number(factura?.importe_facturado_real ?? factura?.fd_total ?? 0);
  const delta = importeReal - (presupuesto || 0);
  const deltaPct = presupuesto > 0 ? (delta / presupuesto) * 100 : 0;
  const muyDistinto = Math.abs(deltaPct) >= 10;
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (factura.fd_pdf_url) window.open(factura.fd_pdf_url, "_blank");
  };
  return (
    <div className="mt-1 flex items-center gap-1.5 flex-wrap text-[10px]" data-testid={`factura-fd-${codigo}`}>
      <button onClick={handleClick}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-300 border border-sky-500/40 hover:bg-sky-500/30 transition"
        title={factura.fd_pdf_url ? "Abrir PDF de la factura" : factura.fd_numero}>
        ✅ {factura.fd_numero}
      </button>
      <span className={muyDistinto ? "text-amber-300 font-semibold" : "text-zinc-400"}
        title={presupuesto > 0 ? `Presupuesto: ${presupuesto.toFixed(2)}€ · Facturado: ${importeReal.toFixed(2)}€` : ""}>
        {importeReal.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
        {presupuesto > 0 && delta !== 0 && (
          <span className={`ml-1 text-[9px] ${delta > 0 ? "text-emerald-400" : "text-rose-400"}`}>
            {delta > 0 ? "▲" : "▼"}{Math.abs(deltaPct).toFixed(0)}%
          </span>
        )}
      </span>
    </div>
  );
}

